import React, {
  useEffect,
  useState,
  useMemo,
  useContext,
  useCallback
} from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  Stack,
  Pagination,
  TableSortLabel,
  useTheme,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import {
  useListOrdersByStore,
  useFetchAllOrdersByStoreForExport
} from "../../../hooks/api/useSalesOrder";
import NoSalesOrderFound from "./NoSalesOrderFound";
import { Order } from "../types/salesOrder";
import { OrderStatus } from "../enums/orderStatus";
import AddSalesOrderModal from "./AddSalesOrderModal";
import SalesOrderListToolbar from "./SalesOrderListToolbar";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  PAGE_PORTAL_ORDERS_SALES_DETAIL,
  PORTAL_PREFIX,
  KivuI18nContext,
  localizedPath,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import {
  formatOrderStatusLabel,
  getOrderStatusTextColor,
  formatDate
} from "../utils/orderUtils";
import { useFormatCurrency } from "../../../hooks/useFormatCurrency";
import ExportData from "../../common/ExportData/ExportData";
import { exportSalesOrderColumns } from "../constants/exportColumns";
import type { SortOrder } from "../interfaces/list";
import { useGetProfile } from "../../../hooks/api/useProfile";
import { useReportError } from "../../../hooks/useReportError";
import CommonLoader from "../../common/CommonLoader/CommonLoader";

import {
  LOG_LEVEL,
  buildLogContext,
  sendAppLog
} from "../../../Utils/appLogger";

/** Client-side search — orderNumber, customerId, status (raw enum + localized label; table + export stay in sync). */
function filterOrders(
  orders: Order[],
  search: string,
  t: (key: string, defaultValue: string) => string
): Order[] {
  if (!search.trim()) return orders;
  const q = search.toLowerCase();
  return orders.filter((o) => {
    const statusRaw = (o.status ?? "").toLowerCase();
    const statusDisplay = formatOrderStatusLabel(o.status, t).toLowerCase();
    return (
      o.orderNumber.toLowerCase().includes(q) ||
      (o.customerId ?? "").toLowerCase().includes(q) ||
      statusRaw.includes(q) ||
      statusDisplay.includes(q)
    );
  });
}

/** Sort by column and direction (table + full export must stay in sync). */
function sortOrders(
  orders: Order[],
  orderBy: keyof Order,
  sortOrder: SortOrder
): Order[] {
  return [...orders].sort((a, b) => {
    const aValue = a[orderBy] ?? "";
    const bValue = b[orderBy] ?? "";
    if (typeof aValue === "number" && typeof bValue === "number") {
      return sortOrder === "asc" ? aValue - bValue : bValue - aValue;
    }
    if (aValue instanceof Date && bValue instanceof Date) {
      return sortOrder === "asc"
        ? aValue.getTime() - bValue.getTime()
        : bValue.getTime() - aValue.getTime();
    }
    return sortOrder === "asc"
      ? String(aValue).localeCompare(String(bValue))
      : String(bValue).localeCompare(String(aValue));
  });
}

const SalesOrderList: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useKivunovaTranslation();
  const { language, defaultLang } = useContext(KivuI18nContext) || {};

  const { salesOrderList: orderList = [], salesOrderListMeta } =
    useSelector((state: RootState) => state.salesOrders) || {};
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  const [openAddModal, setOpenAddModal] = useState(false);
  // Track whether the initial fetch has completed so we don't flash "empty"
  // while the request is still in flight.
  const [fetchDone, setFetchDone] = useState(false);

  const listOrdersByStore = useListOrdersByStore();
  const fetchAllOrdersForExport = useFetchAllOrdersByStoreForExport();

  const theme = useTheme();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));

  const [search, setSearch] = useState("");
  /** 0-based page index for the API (default page size 50). */
  const [serverPage, setServerPage] = useState(0);
  /** Empty string = all statuses (server does not filter by status). */
  const [statusFilter, setStatusFilter] = useState<OrderStatus | "">("");
  const [order, setOrder] = useState<SortOrder>("desc");
  const [orderBy, setOrderBy] = useState<keyof Order>("createdAt");
  const DEFAULT_PAGE_SIZE = 50;
  const getProfileMutation = useGetProfile();
  const reportError = useReportError();
  const formatCurrency = useFormatCurrency();

  useEffect(() => {
    (async () => {
      try {
        await listOrdersByStore.mutateAsync({
          page: serverPage,
          pageSize: DEFAULT_PAGE_SIZE,
          ...(statusFilter ? { status: statusFilter } : {})
        });
      } catch (err) {
        reportError("Failed to fetch sales order list (UI)", err, {
          component: "SalesOrderList"
        });
      } finally {
        setFetchDone(true);
      }
    })();
  }, [reportError, serverPage, statusFilter]);

  useEffect(() => {
    const loadUserDetails = async () => {
      try {
        await getProfileMutation.mutateAsync();
      } catch (err) {
        reportError("Failed to load user profile on sales order list", err, {
          component: "SalesOrderList"
        });
      }
    };
    loadUserDetails();
  }, [reportError]);

  /**
   * handleNewOrder
   *
   * All roles: always open the modal.
   * The modal handles store picker visibility (COMPANY_ADMIN only)
   * and customer dropdown (all roles).
   */
  const handleNewOrder = () => {
    setOpenAddModal(true);
  };

  const handleOrderCreated = (orderId: string) => {
    const route = PAGE_PORTAL_ORDERS_SALES_DETAIL.replace(":orderId", orderId);
    navigate(
      localizedPath(`/${PORTAL_PREFIX}/${route}`, language, defaultLang)
    );
  };

  const filteredList = useMemo(
    () => filterOrders(orderList, search, t),
    [orderList, search, t]
  );

  const sortedList = useMemo(
    () => sortOrders(filteredList, orderBy, order),
    [filteredList, order, orderBy]
  );

  /** Full result set for export: all API pages, then same search + sort as the table. */
  const fetchSalesOrdersForExport = useCallback(async () => {
    const all = await fetchAllOrdersForExport({
      pageSize: DEFAULT_PAGE_SIZE,
      ...(statusFilter ? { status: statusFilter } : {})
    });
    return sortOrders(filterOrders(all, search, t), orderBy, order);
  }, [fetchAllOrdersForExport, statusFilter, search, order, orderBy, t]);

  const handleSort = (property: keyof Order) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  /** First paint only — avoid replacing the whole page when filter/pagination refetches. */
  const isInitialLoading = !fetchDone;
  /** Filter/page change: keep toolbar, filter, and pagination mounted; show overlay on table. */
  const isListRefreshing = listOrdersByStore.isPending && fetchDone;

  const tableColSpan = matchMD ? 4 : 5;

  if (isInitialLoading) {
    return (
      <CommonLoader
        loadingMessage={t(
          "sales-order-list-loading",
          "Loading sales orders..."
        )}
      />
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3 },
        display: "flex",
        flexDirection: "column",
        gap: 3
      }}
    >
      <SalesOrderListToolbar
        title={t("sales-order-list-title", "Sales Orders")}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder={t(
          "sales-order-list-search-placeholder",
          "Search by order number, customer ID, or status"
        )}
        onNewOrder={handleNewOrder}
        isNewOrderLoading={false}
        isMobile={matchMD}
      >
        {/* Export file rows come from fetchSalesOrdersForExport (all API pages); `data` is unused when fetch is set. */}
        <ExportData
          data={[] as Order[]}
          fetchDataForExport={fetchSalesOrdersForExport}
          fullDatasetExportHint={t(
            "sales-order-export-full-dataset-hint",
            "Export loads every order from the server for the selected status, then applies your search and sort — not only this page."
          )}
          columns={exportSalesOrderColumns}
          filename={`sales_orders_${new Date().toISOString().split("T")[0]}`}
          title={t("sales-order-list-export-title", "Export Sales Orders")}
          buttonVariant="outlined"
          buttonText={t("sales-order-list-export-button", "Export")}
          formats={["csv", "xlsx", "pdf", "json"]}
          onExportComplete={(format, recordCount) => {
            sendAppLog(
              LOG_LEVEL.INFO,
              `Exported ${recordCount ?? 0} sales orders as ${format}`,
              buildLogContext(
                "sales-order",
                "export-complete",
                userDetails?.id,
                userDetails?.companyId,
                userDetails?.storeId,
                {
                  salesOrderCount: recordCount ?? 0,
                  format
                }
              )
            );
          }}
          onExportError={(error) => {
            reportError("Sales order list export failed", error, {
              component: "SalesOrderList"
            });
          }}
        />
      </SalesOrderListToolbar>

      <Box
        sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}
      >
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="sales-order-status-filter-label">
            {t("sales-order-list-status-filter", "Status")}
          </InputLabel>
          <Select
            labelId="sales-order-status-filter-label"
            label={t("sales-order-list-status-filter", "Status")}
            value={statusFilter}
            disabled={isListRefreshing}
            onChange={(e) => {
              const v = e.target.value as OrderStatus | "";
              setStatusFilter(v);
              setServerPage(0);
            }}
          >
            <MenuItem value="">
              <em>{t("sales-order-list-status-all", "All statuses")}</em>
            </MenuItem>
            {Object.values(OrderStatus).map((s) => (
              <MenuItem key={s} value={s}>
                {formatOrderStatusLabel(s, t)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Table — overlay spinner on refetch so filters/pagination stay visible */}
      <Box sx={{ position: "relative" }}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ pl: 2 }}>
                  <TableSortLabel
                    active={orderBy === "orderNumber"}
                    direction={orderBy === "orderNumber" ? order : "asc"}
                    onClick={() => handleSort("orderNumber")}
                  >
                    {t("sales-order-list-col-order-number", "Order Number")}
                  </TableSortLabel>
                </TableCell>
                {!matchMD && (
                  <TableCell sx={{ px: 2 }}>
                    {t("sales-order-list-col-total-amount", "Total Amount")}
                  </TableCell>
                )}
                <TableCell sx={{ px: 2 }}>
                  <TableSortLabel
                    active={orderBy === "status"}
                    direction={orderBy === "status" ? order : "asc"}
                    onClick={() => handleSort("status")}
                  >
                    {t("sales-order-list-col-status", "Status")}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ px: 2, pr: 2 }}>
                  <TableSortLabel
                    active={orderBy === "createdAt"}
                    direction={orderBy === "createdAt" ? order : "asc"}
                    onClick={() => handleSort("createdAt")}
                  >
                    {t("sales-order-list-col-created", "Created")}
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" sx={{ px: 2 }}>
                  {t("sales-order-list-col-actions", "Actions")}
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {sortedList.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={tableColSpan}
                    align="center"
                    sx={{ border: 0, verticalAlign: "middle", px: 2 }}
                  >
                    {orderList.length === 0 ? (
                      statusFilter ? (
                        <Typography color="text.secondary" sx={{ py: 4 }}>
                          {t(
                            "sales-order-list-no-status-matches",
                            "No orders with the selected status. Try another status or clear the filter."
                          )}
                        </Typography>
                      ) : (
                        <NoSalesOrderFound compact />
                      )
                    ) : (
                      <Typography color="text.secondary" sx={{ py: 4 }}>
                        {t(
                          "sales-order-list-no-search-matches",
                          "No orders match your search."
                        )}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                sortedList.map((o) => (
                  <TableRow key={o.orderId} hover>
                    <TableCell sx={{ fontSize: 13, fontWeight: 600, pl: 2 }}>
                      {o.orderNumber}
                    </TableCell>
                    {!matchMD && (
                      <TableCell sx={{ fontSize: 13, px: 2 }}>
                        {formatCurrency(o.totalAmount)}
                      </TableCell>
                    )}
                    <TableCell sx={{ fontSize: 13, px: 2 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: getOrderStatusTextColor(o.status),
                          fontWeight: 500,
                          fontSize: 13
                        }}
                      >
                        {formatOrderStatusLabel(o.status, t)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: 13, px: 2, pr: 2 }}>
                      {formatDate(o.createdAt)}
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: 13, px: 2 }}>
                      <IconButton
                        color="primary"
                        onClick={() => {
                          const route = PAGE_PORTAL_ORDERS_SALES_DETAIL.replace(
                            ":orderId",
                            o.orderId
                          );
                          navigate(
                            localizedPath(
                              `/${PORTAL_PREFIX}/${route}`,
                              language,
                              defaultLang
                            )
                          );
                        }}
                      >
                        <VisibilityOutlinedIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {isListRefreshing && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              borderRadius: 1,
              bgcolor: (theme) => alpha(theme.palette.background.paper, 0.72),
              zIndex: 1
            }}
          >
            <CircularProgress size={40} thickness={4} />
            <Typography variant="body2" color="text.secondary">
              {t("sales-order-list-refreshing", "Updating list…")}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Server-driven pagination */}
      {salesOrderListMeta && salesOrderListMeta.totalPages > 1 && (
        <Stack spacing={2} alignItems="center" mt={3}>
          <Pagination
            count={salesOrderListMeta.totalPages}
            page={salesOrderListMeta.page + 1}
            onChange={(_, value) => setServerPage(value - 1)}
            color="primary"
            disabled={isListRefreshing}
          />
        </Stack>
      )}

      {/* Add Order Modal */}
      <AddSalesOrderModal
        open={openAddModal}
        onClose={() => setOpenAddModal(false)}
        onOrderCreated={handleOrderCreated}
      />
    </Box>
  );
};

export default SalesOrderList;
